export interface Patient {
  id: string;
  name: string;
  nameRomaji: string; // 【変更】ローマ字（検索用）
  // Soft-delete support: "archived" patients are hidden from lists but kept for history.
  isActive?: boolean;
  patientNumber?: string;
  phoneNumber?: string;
  patientPhoneNumber?: string; // 患者電話番号（保護者あり/なしに関わらず）
  birthDate?: string | null;
  zipcode?: string; // 【追加】郵便番号
  address?: string;
  hasGuardian?: boolean; // 保護者ありフラグ
  guardianName?: string; // 保護者名
  guardianNameRomaji?: string; // 保護者ローマ字
  guardianContact?: string; // 保護者連絡先
  guardianRelationship?: string; // 保護者関係（任意）
  medicalHistory?: string;
  allergies?: string;
  emergencyContact?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName?: string;
  updatedBy: string;
  updatedByName?: string;
  // 【追加】患者一覧表示に必要なプロパティ
  nextAppointment?: string;
  doctorId?: string;
}

export interface NewPatientData {
  name: string;
  nameRomaji: string;
  zipcode: string;
  address: string;
  patientNumber?: string; // 患者番号（0000-0000形式）
  patientPhoneNumber?: string;
  hasGuardian?: boolean;
  guardianName?: string;
  guardianNameRomaji?: string;
  guardianContact?: string;
  guardianRelationship?: string;
}

export interface Handover {
  id: string;
  patientId: string;
  patientName: string;
  content: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  isCompleted: boolean;
}

// 【更新】Staffインターフェースをロールと職種に分離
export interface Staff {
  id: string;
  name: string;
  nameRomaji: string;
  role: 'developer' | 'admin' | 'staff';
  jobTitle: string;
  pin: string;
  phoneNumber: string;
  authUserId?: string | null;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName?: string;
  isActive: boolean;
}

export interface NewStaffData {
  name: string;
  nameRomaji: string;
  pin: string;
  role: 'admin' | 'staff';
  jobTitle: string;
  phoneNumber: string;
  /**
   * Optional email for Supabase Auth onboarding (magic link invite).
   * NOTE: UI must not change; this field is supplied only when available.
   */
  email?: string;
}

// DBから直接取得する予約データの型（生データ）
export interface AppointmentRow {
  id: string;
  doctor_id: string;
  doctor_name?: string | null;
  patient_id: string;
  appointment_date: string; // YYYY-MM-DD
  appointment_time: string; // HH:mm
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// 予約作成時の入力型（DB挿入用）
export interface AppointmentRowInput {
  doctor_id: string;
  doctor_name?: string;
  patient_id: string;
  appointment_date: string; // YYYY-MM-DD
  appointment_time: string; // HH:mm
  status: string;
  notes: string;
}

// フロントエンドで使用する予約データの型
export interface Appointment {
  id: string;
  doctorId: string;
  doctorName?: string;
  patientId: string;
  appointmentTime: string;
  appointmentDate: string;
  type: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  notes: string;
  createdAt: string;
  updatedAt: string;
}

// 予約バリデーションエラーコード
export type AppointmentValidationErrorCode =
  | 'PAST_DATETIME'
  | 'OUT_OF_BUSINESS_HOURS'
  | 'SLOT_MISALIGNED'
  | 'DOUBLE_BOOKED'
  | 'FOREIGN_KEY_MISSING'
  | 'SAME_PATIENT_SAME_DATE';

export interface AppointmentValidationError {
  code: AppointmentValidationErrorCode;
  message: string;
}

// カスタムエラークラス
export class AppointmentError extends Error {
  code: AppointmentValidationErrorCode;
  validationError?: AppointmentValidationError;

  constructor(
    code: AppointmentValidationErrorCode,
    message: string,
    validationError?: AppointmentValidationError
  ) {
    super(message);
    this.name = 'AppointmentError';
    this.code = code;
    this.validationError = validationError;
    // TypeScriptでErrorを継承する際に必要
    Object.setPrototypeOf(this, AppointmentError.prototype);
  }
}

// 会員（医院代表者）の型定義
export interface Member {
  id: string;
  email: string;
  clinicId: string;
  clinicName: string | null;
  plan: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 会員登録時の入力データ型
/** 医院別・音声入力用語（申し送りの認識文法とマージ） */
export interface ClinicSpeechTerm {
  id: string;
  clinicId: string;
  phrase: string;
  reading: string;
  createdAt: string;
  updatedAt: string;
  createdByStaffId: string | null;
}

export interface NewMemberData {
  email: string;
  password: string;
  clinicName: string;
  zipcode: string; // 郵便番号（clinic_id生成用）
}

// 会員ログイン時の入力データ型
export interface MemberLoginData {
  email: string;
  password: string;
}

